import type { ChatMessage } from 'shared/src/api/ai-chat-types';
import type { VisualizationBlockProps } from './visualization/visualization-block';

type MessagePart = ChatMessage['parts'][number];

export type MessageSegment =
  | { type: 'text'; texts: string[] }
  | { type: 'viz'; props: VisualizationBlockProps };

/** Splits parts into ordered segments: `text` (rendered in a bubble) and `viz` (rendered standalone, no bubble). */
export function buildMessageSegments(parts: MessagePart[]): MessageSegment[] {
  let state = EMPTY_STATE;
  for (const part of parts) {
    state = applyPart(state, part);
  }
  return flushText(flushViz(state)).segments;
}

/**
 * Folded over the parts in order: text parts accumulate into the current bubble,
 * while the `data-*` parts build up a single open viz that the next text (or the
 * next viz, or the end of the stream) flushes into its own standalone segment.
 */
type SegmentState = {
  segments: MessageSegment[];
  pendingTexts: string[];
  openViz: VisualizationBlockProps | null;
};

const EMPTY_STATE: SegmentState = { segments: [], pendingTexts: [], openViz: null };

function applyPart(state: SegmentState, part: MessagePart): SegmentState {
  switch (part.type) {
    case 'data-cube-query':
      return { ...flushViz(flushText(state)), openViz: { cubeQuery: part.data } };
    case 'data-viz-status':
      return state.openViz
        ? { ...state, openViz: { ...state.openViz, vizStatus: part.data.status } }
        : state;
    case 'data-visualization':
      return state.openViz
        ? { ...state, openViz: { ...state.openViz, visualization: part.data } }
        : state;
    case 'data-viz-error':
      return { ...state, openViz: null };
    case 'text':
      // Text ends any open viz group, then joins the current bubble.
      return appendText(flushViz(state), part.text);
    default:
      return state; // tool-* parts are intentionally ignored
  }
}

function flushText(state: SegmentState): SegmentState {
  if (state.pendingTexts.length === 0) return state;
  return {
    ...state,
    segments: [...state.segments, { type: 'text', texts: state.pendingTexts }],
    pendingTexts: []
  };
}

function flushViz(state: SegmentState): SegmentState {
  if (!state.openViz) return state;
  return {
    ...state,
    segments: [...state.segments, { type: 'viz', props: state.openViz }],
    openViz: null
  };
}

function appendText(state: SegmentState, text: string): SegmentState {
  return { ...state, pendingTexts: [...state.pendingTexts, text] };
}
